import React from 'react';
import { useTheme as useBasicTheme } from '@/components/theme-provider';
import { useTheme as useEnhancedTheme, defaultTheme } from '@/contexts/ThemeProvider';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { useToast } from '@/hooks/use-toast';
import { availableFonts } from './lib/font-loader';
import { Sun, Moon, Monitor, Contrast, Type, Palette, RotateCcw } from 'lucide-react';

/**
 * Unified Theme Settings Component
 *
 * Theme mode is controlled by the basic theme provider (globals.css tokens).
 * Enhanced theme covers typography and density only.
 */
export const UnifiedThemeSettings: React.FC = () => {
  const { theme: basicTheme, setTheme: setBasicTheme } = useBasicTheme();
  const { theme: enhancedTheme, setTheme: setEnhancedTheme } = useEnhancedTheme();
  const { toast } = useToast();

  const handleBasicThemeChange = (newTheme: 'dark' | 'light' | 'system' | 'high-contrast' | 'glass') => {
    setBasicTheme(newTheme);
  };

  const handleFontChange = (value: string) => {
    const updated = {
      ...enhancedTheme,
      typography: { ...enhancedTheme.typography, fontFamily: value },
    };
    setEnhancedTheme(updated);
    toast({
      title: 'Font updated',
      description: 'Font family has been applied.',
    });
  };

  const handleReset = () => {
    setBasicTheme('light');
    setEnhancedTheme(defaultTheme);
    toast({
      title: 'Theme reset',
      description: 'All theme settings have been reset to defaults.',
    });
  };

  return (
    <div className="space-y-5">
      {/* Theme Mode Selection */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Sun className="h-4 w-4 text-muted-foreground" />
          <Label className="text-sm font-medium">Theme Mode</Label>
        </div>
        <RadioGroup value={basicTheme} onValueChange={handleBasicThemeChange}>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <Label
              htmlFor="light-theme"
              className={`flex flex-col items-center justify-center rounded-lg border-2 p-3 cursor-pointer transition-all ${
                basicTheme === 'light'
                  ? 'border-primary bg-primary/10'
                  : 'border-muted bg-card hover:bg-accent'
              }`}
            >
              <RadioGroupItem value="light" id="light-theme" className="sr-only" />
              <Sun className={`h-5 w-5 mb-1.5 ${basicTheme === 'light' ? 'text-primary' : ''}`} />
              <span className="text-xs font-medium">Light</span>
            </Label>

            <Label
              htmlFor="dark-theme"
              className={`flex flex-col items-center justify-center rounded-lg border-2 p-3 cursor-pointer transition-all ${
                basicTheme === 'dark'
                  ? 'border-primary bg-primary/10'
                  : 'border-muted bg-card hover:bg-accent'
              }`}
            >
              <RadioGroupItem value="dark" id="dark-theme" className="sr-only" />
              <Moon className={`h-5 w-5 mb-1.5 ${basicTheme === 'dark' ? 'text-primary' : ''}`} />
              <span className="text-xs font-medium">Dark</span>
            </Label>

            <Label
              htmlFor="system-theme"
              className={`flex flex-col items-center justify-center rounded-lg border-2 p-3 cursor-pointer transition-all ${
                basicTheme === 'system'
                  ? 'border-primary bg-primary/10'
                  : 'border-muted bg-card hover:bg-accent'
              }`}
            >
              <RadioGroupItem value="system" id="system-theme" className="sr-only" />
              <Monitor className={`h-5 w-5 mb-1.5 ${basicTheme === 'system' ? 'text-primary' : ''}`} />
              <span className="text-xs font-medium">System</span>
            </Label>

            <Label
              htmlFor="high-contrast-theme"
              className={`flex flex-col items-center justify-center rounded-lg border-2 p-3 cursor-pointer transition-all ${
                basicTheme === 'high-contrast'
                  ? 'border-primary bg-primary/10'
                  : 'border-muted bg-card hover:bg-accent'
              }`}
            >
              <RadioGroupItem value="high-contrast" id="high-contrast-theme" className="sr-only" />
              <Contrast className={`h-5 w-5 mb-1.5 ${basicTheme === 'high-contrast' ? 'text-primary' : ''}`} />
              <span className="text-xs font-medium">High Contrast</span>
            </Label>

            <Label
              htmlFor="glass-theme"
              className={`flex flex-col items-center justify-center rounded-lg border-2 p-3 cursor-pointer transition-all ${
                basicTheme === 'glass'
                  ? 'border-primary bg-primary/10'
                  : 'border-muted bg-card hover:bg-accent'
              }`}
            >
              <RadioGroupItem value="glass" id="glass-theme" className="sr-only" />
              <Palette className={`h-5 w-5 mb-1.5 ${basicTheme === 'glass' ? 'text-primary' : ''}`} />
              <span className="text-xs font-medium">Glass</span>
            </Label>
          </div>
        </RadioGroup>
      </div>

      <Separator />

      {/* Typography */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Type className="h-4 w-4 text-muted-foreground" />
          <Label htmlFor="font-family" className="text-sm font-medium">Typography</Label>
        </div>
        <Select
          value={enhancedTheme.typography.fontFamily}
          onValueChange={handleFontChange}
        >
          <SelectTrigger id="font-family" className="w-full h-10">
            <SelectValue placeholder="Select font" />
          </SelectTrigger>
          <SelectContent>
            {availableFonts.map((font) => (
              <SelectItem key={font.value} value={font.value}>
                {font.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Separator />

      {/* Reset Button */}
      <div className="flex justify-end pt-1">
        <Button
          variant="outline"
          size="sm"
          onClick={handleReset}
          className="gap-2 h-9"
        >
          <RotateCcw className="h-3.5 w-3.5" />
          Reset to Defaults
        </Button>
      </div>
    </div>
  );
};

export default UnifiedThemeSettings;
